import { Avatar, Button } from '@mui/material';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { BasicAccountFragment, useGetAccountsQuery } from '../../generated/types';
import { stringAvatar } from '../../shared/utils/string/username';
import { enqueueSnackbar } from 'notistack';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import ZenLoading from '../loading/Loading';

export type BasicAccount = BasicAccountFragment;

export interface AccountSelectProps {
    title?: string;
    subtitle?: string;
    onSelect?: (account: BasicAccount) => void;
    hideHeader?: boolean;
    addButtonText?: string;
}

const AccountSelect: React.FC<AccountSelectProps> = ({
    title = 'Select Account',
    subtitle,
    hideHeader,
    onSelect,
    addButtonText = 'Create a new Restaurant',
}) => {
    const [selectedAccount, setSelectedAccount] = useState<BasicAccount | null>(null);
    const { pathname } = useLocation();
    const history = useHistory();
    const accountId = Number(pathname.split('/')[2]) || 0;
    const { loading: isLoading, data } = useGetAccountsQuery({
        variables: { limit: 100, offset: 0 },
        onError: (e) => {
            enqueueSnackbar(e.message, { variant: 'error' });
        },
    });

    const onSelectAccount = (account: BasicAccount) => {
        if (account.id !== selectedAccount?.id) {
            onSelect && onSelect(account);
        }
    };

    const onClickCreateNewRestaurant = () => {
        sessionStorage.removeItem('init_account_id');
        history.push('/getstarted');
    };
    useEffect(() => {
        if (accountId && data?.accounts) {
            const currentAccount = data?.accounts.find((account) => account?.id === accountId);
            if (currentAccount) {
                setSelectedAccount(currentAccount);
            }
        }
    }, [data?.accounts, accountId]);

    return (
        <>
            {isLoading ? (
                <div className={`flex flex-auto items-center justify-center`}>
                    <ZenLoading />
                </div>
            ) : (
                <div className="flex flex-col flex-auto overflow-y-auto">
                    {!hideHeader && (
                        <div className="mb-20 text-center flex-shrink-0">
                            {title && <div className="font-bold text-24">{title}</div>}
                            {subtitle && <div className="text-14 text-primary-50 mt-8">{subtitle}</div>}
                        </div>
                    )}
                    <div className="flex-auto flex flex-col overflow-y-auto px-3">
                        {data?.accounts &&
                            data.accounts.length > 0 &&
                            data.accounts.map((account, index) => (
                                <div
                                    key={index}
                                    className={classNames({
                                        'rounded-8 my-8 cursor-pointer py-12 px-16': true,
                                        'border-2 border-success-mid bg-gradient-light':
                                            selectedAccount && account.id === selectedAccount.id,
                                        'border-2 border-transparent shadow-account':
                                            account.id !== selectedAccount?.id || !selectedAccount,
                                    })}
                                    onClick={() => onSelectAccount(account)}
                                >
                                    <div className="flex items-center">
                                        {account?.logo ? (
                                            <Avatar
                                                sx={{ width: 35, height: 35 }}
                                                variant="rounded"
                                                alt="avatar"
                                                src={account.logo}
                                                className="border border-primary-40"
                                            />
                                        ) : (
                                            <Avatar
                                                sx={{ width: 35, height: 35 }}
                                                variant="rounded"
                                                className="border border-primary-40"
                                                {...stringAvatar(account.name)}
                                            />
                                        )}
                                        <div className="ml-8 flex-auto text-18 leading-none truncate">
                                            {account.name}
                                        </div>
                                    </div>
                                </div>
                            ))}
                    </div>
                    <div className="mt-8 flex-shrink-0">
                        <Button
                            variant="dashed"
                            size="large"
                            color="success"
                            fullWidth
                            onClick={onClickCreateNewRestaurant}
                        >
                            <FontAwesomeIcon size={'2xs'} icon={faPlus} />
                            <span className={`px-10 text-18 font-semibold leading-20`}>{addButtonText}</span>
                        </Button>
                    </div>
                </div>
            )}
        </>
    );
};

export default AccountSelect;
